import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ClipboardDocumentIcon } from "@heroicons/react/24/outline";
import FilesRows from "../../components/FilesRows";
import api from "../../api";
import OnboardingProgressBar from "../OnboardingProgressBar";
import OnboardingWrapper from "../../components/OnboardingWrapper";
import PrimaryButton from "../../components/PrimaryButton";
import SecondaryButton from "../../components/SecondaryButton";
import UploadFilesModal from "../../components/UploadFilesModal";
import PrimaryLinkButton from "../../components/PrimaryLinkButton";
import { useCompanyApi } from "../../hooks/useCompanyApi";
import useUploadingProgress from "../../hooks/useUploadingProgress";

const GmailSupport = () => {
  const navigate = useNavigate();

  const { updateCompany, company, fetchCompany } = useCompanyApi();

  const [files, setFiles] = useState([]);
  const [showUploadModal, setShowUploadModal] = useState(false);

  const { progress, setProgress } = useUploadingProgress({
    company,
    count: files.length,
  });

  const [loading, setLoading] = useState(false);

  const upload = async file => {
    if (!file) {
      alert("No file selected");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    try {
      await api.post("/api/upload-file", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
    } catch (error) {
      console.error("Error uploading file:", error);
    }
  };

  const onSubmit = async e => {
    try {
      e.preventDefault();
      setProgress(0);
      setLoading(true);

      const total = files.length;

      for (let i = 0; i < total; i++) {
        try {
          await upload(files[i]);
        } catch (error) {
          console.warn(`Failed to upload file: ${files[i]?.name}`, error);
        }
      }

      await updateCompany({
        registration_step: "test",
      });

      setTimeout(() => navigate("/onboarding/test"), 500);
    } catch (error) {
      alert(error.message);
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 500);
    }
  };

  const onSkip = async () => {
    await updateCompany({
      registration_step: "test",
    });

    navigate("/onboarding/test");
  };

  const removeFile = async idx => {
    setFiles(prev => prev.filter((_, _idx) => _idx !== idx));
  };

  const submitFiles = files => setFiles(prev => [...prev, ...files]);

  const disabled = files.length === 0;

  useEffect(() => {
    fetchCompany();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <OnboardingWrapper
      onSubmit={onSubmit}
      actions={
        <>
          <div className="pb-20 justify-center w-full hidden sm:flex">
            <OnboardingProgressBar currentStep={2} />
          </div>

          <div className="sm:hidden w-full flex flex-col gap-4 items-center">
            <PrimaryButton disabled={disabled} loading={loading}>
              Continue
            </PrimaryButton>

            <PrimaryLinkButton onClick={onSkip} type="button">
              Skip this step
            </PrimaryLinkButton>
          </div>
        </>
      }
    >
      <div className="sm:max-w-md w-full flex flex-col gap-6 mt-8 sm:mt-0 px-4 sm:px-0 mb-6 sm:mb-0">
        <div>
          <h1 className="text-3xl font-semibold leading-9 text-gray-900 text-center">
            Upload content
          </h1>
          <p className="text-base font-normal leading-normal text-center text-slate-600">
            Upload your knowledge base to train your AI agent
          </p>
        </div>

        <SecondaryButton
          onClick={() => setShowUploadModal(true)}
          type="button"
          startIcon={<ClipboardDocumentIcon className="size-6" />}
        >
          Upload files (pdf, csv, xlsx, etc.)
        </SecondaryButton>

        {files.length > 0 && <FilesRows files={files} onRemove={removeFile} />}

        {loading && (
          <div className="flex items-center gap-4 mt-5 sm:mt-6">
            <div className="overflow-hidden rounded-full bg-gray-200 flex-1">
              <div
                style={{ width: `${progress}%` }}
                className="h-2 rounded-full bg-primary transition-all"
              />
            </div>

            <span className="text-xs font-semibold">{progress}%</span>
          </div>
        )}

        <div className="hidden sm:block">
          <PrimaryButton disabled={disabled} loading={loading}>
            Continue
          </PrimaryButton>
        </div>

        <div className="hidden sm:flex justify-center">
          <PrimaryLinkButton onClick={onSkip} type="button">
            Skip this step
          </PrimaryLinkButton>
        </div>
      </div>

      <UploadFilesModal
        open={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onSubmit={submitFiles}
      />
    </OnboardingWrapper>
  );
};

export default GmailSupport;
